import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { storage } from '../utils/storage';
import { Platform } from '../utils/platform';
import { supabase } from '../services/supabase';
import { useAuthStore } from './useAuthStore';
import { useSupabaseSubscription } from '../hooks/useSupabaseSubscription';

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

export interface Payee {
  id: string;
  user_id: string;
  name: string;
  type: string;
  icon?: string;
  color?: string;
  website?: string;
  notes?: string;
  total_transactions: number;
  created_at: string;
  updated_at: string;
}

interface PayeesState {
  payees: Payee[];
  isLoading: boolean;
  error: string | null;
  lastFetchTime: number | null;
  fetchPayees: (userId: string, force?: boolean) => Promise<void>;
  addPayee: (data: Omit<Payee, 'id' | 'created_at' | 'updated_at' | 'total_transactions'>) => Promise<void>;
  updatePayee: (payeeId: string, data: Partial<Omit<Payee, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'total_transactions'>>) => Promise<void>;
  deletePayee: (userId: string, payeeId: string) => Promise<void>;
  isPayeeNameExists: (userId: string, name: string, excludeId?: string) => Promise<boolean>;
  clearPayees: () => void;
}

export const usePayeesStore = create<PayeesState>()(
  persist(
    (set, get) => ({
      payees: [],
      isLoading: false,
      error: null,
      lastFetchTime: null,

      fetchPayees: async (userId: string, force: boolean = false) => {
        if (!userId) return;
        
        // Check if we have recent data (within last 5 minutes) to avoid unnecessary fetches
        // Skip the cache check if force=true
        if (!force) {
          const now = Date.now();
          const fiveMinutesAgo = now - 5 * 60 * 1000;
          const lastFetch = get().lastFetchTime;
          
          if (lastFetch && lastFetch > fiveMinutesAgo && get().payees.length > 0) {
            console.log('Using cached payees data, last fetched at:', new Date(lastFetch).toLocaleTimeString());
            return;
          }
        } else {
          console.log('Force refreshing payees data');
        }
        
        set({ isLoading: true, error: null });
        try {
          // Fetch payees and transaction counts in a more efficient way
          const { data: payees, error: payeesError } = await supabase
            .from('payees')
            .select('*')
            .eq('user_id', userId)
            .order('name');

          if (payeesError) throw payeesError;
          
          if (!payees || payees.length === 0) {
            set({ 
              payees: [], 
              isLoading: false,
              lastFetchTime: Date.now()
            });
            return;
          }
          
          // Get transaction counts for all payees in one query
          const payeeIds = payees.map(p => p.id);
          const { data: transactionCounts, error: countError } = await supabase
            .from('transactions')
            .select('payee_id')
            .eq('user_id', userId)
            .in('payee_id', payeeIds);
          
          if (countError) {
            console.warn('Error counting transactions:', countError);
          }
          
          // Count transactions per payee
          const countsMap = new Map<string, number>();
          (transactionCounts || []).forEach(transaction => {
            if (transaction.payee_id) {
              countsMap.set(transaction.payee_id, (countsMap.get(transaction.payee_id) || 0) + 1);
            }
          });
          
          // Combine payees with their transaction counts
          const payeesWithCounts = payees.map(payee => ({
            ...payee,
            total_transactions: countsMap.get(payee.id) || 0
          }));
          
          set({ 
            payees: payeesWithCounts, 
            isLoading: false,
            lastFetchTime: Date.now()
          });
        } catch (error) {
          console.error('Error fetching payees:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch payees',
            isLoading: false 
          });
        }
      },

      addPayee: async (data) => {
        if (!data.user_id) return;
        
        set({ isLoading: true, error: null });
        try {
          // Check if name exists
          const exists = await get().isPayeeNameExists(data.user_id, data.name);
          if (exists) {
            throw new Error('A payee with this name already exists');
          }

          const { data: payee, error } = await supabase
            .from('payees')
            .insert([{
              ...data,
              total_transactions: 0
            }])
            .select()
            .single();

          if (error) throw error;
          
          set(state => ({
            payees: [payee, ...state.payees].sort((a, b) => a.name.localeCompare(b.name)),
            isLoading: false
          }));
        } catch (error) {
          console.error('Error adding payee:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to add payee',
            isLoading: false 
          });
          throw error;
        }
      },

      updatePayee: async (payeeId, data) => {
        if (!payeeId) return;
        
        set({ isLoading: true, error: null });
        try {
          // Check if name exists (if name is being updated)
          if (data.name) {
            const payee = get().payees.find(p => p.id === payeeId);
            if (payee) {
              const exists = await get().isPayeeNameExists(payee.user_id, data.name, payeeId);
              if (exists) {
                throw new Error('A payee with this name already exists');
              }
            }
          }

          const { data: updatedPayee, error } = await supabase
            .from('payees')
            .update(data)
            .eq('id', payeeId)
            .select()
            .single();

          if (error) throw error;
          
          set(state => ({
            payees: state.payees.map(payee =>
              payee.id === payeeId ? { ...payee, ...updatedPayee } : payee
            ).sort((a, b) => a.name.localeCompare(b.name)),
            isLoading: false
          }));
        } catch (error) {
          console.error('Error updating payee:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to update payee',
            isLoading: false 
          });
          throw error;
        }
      },

      deletePayee: async (userId, payeeId) => {
        if (!userId || !payeeId) return;
        
        set({ isLoading: true, error: null });
        try {
          const { error } = await supabase
            .from('payees')
            .delete()
            .eq('id', payeeId)
            .eq('user_id', userId);

          if (error) throw error;
          
          set(state => ({
            payees: state.payees.filter(p => p.id !== payeeId),
            isLoading: false
          }));
        } catch (error) {
          console.error('Error deleting payee:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to delete payee',
            isLoading: false 
          });
          throw error;
        }
      },

      isPayeeNameExists: async (userId: string, name: string, excludeId?: string) => {
        try {
          const { data: payees, error } = await supabase
            .from('payees')
            .select('id')
            .eq('user_id', userId)
            .eq('name', name.trim());

          if (error) throw error;

          if (excludeId) {
            return (payees || []).some(p => p.id !== excludeId);
          }

          return (payees || []).length > 0;
        } catch (error) {
          console.error('Error checking payee name:', error);
          return false;
        }
      },

      clearPayees: () => {
        set({ payees: [], isLoading: false, error: null, lastFetchTime: null });
      }
    }),
    {
      name: 'flows-payees',
      storage: createJSONStorage(() => getStorage()),
      partialize: (state) => ({
        payees: state.payees,
        lastFetchTime: state.lastFetchTime
      }),
    }
  )
); 

// Re-export the core store for direct access to payees data
export const usePayees = () => {
  const { payees, isLoading, error } = usePayeesStore();
  return { payees, isLoading, error };
};

export const { fetchPayees, clearPayees, deletePayee } = usePayeesStore.getState();

// Hook to subscribe to payee changes
export function usePayeesSubscription() {
  const userId = useAuthStore(state => state.user?.id);
  
  useSupabaseSubscription({
    table: 'payees',
    filter: userId ? `user_id=eq.${userId}` : undefined,
    userId: userId,
    queryKeys: userId ? [['payees', userId]] : [],
    onRecordChange: () => {
      if (userId) {
        console.log('Payees changed, refreshing data...');
        fetchPayees(userId);
      }
    }
  });
} 